import osmtogeojson from "osmtogeojson";
import { searchGeoapify } from "senf-shared/utils/geoapify";

interface PostalCodeCountry {
  postalCode: string;
  countryCode: string;
}

export default () => {
  const getPostalCodeBoundaries = async (
    postalCodesWithCountryCodes: PostalCodeCountry[],
  ) => {
    console.log("Attempting to fetch postal codes with Geoapify first...");

    // Try Geoapify first
    try {
      const geoapifyFeatures = await Promise.all(
        postalCodesWithCountryCodes.map(async ({ postalCode, countryCode }) => {
          try {
            const features = await searchGeoapify(postalCode, {
              countryCode,
              filterTypes: ["postcode"],
            });

            // Validate that we got an exact postcode match
            const exactMatch = features.find((f) => {
              const featurePostcode = f.properties?.postcode;
              const resultType = f.properties?.type || "";

              // Strict validation:
              // 1. Postcode must exactly match
              // 2. Result type must be postcode (not city, district, etc.)
              // 3. Should have city/county but not be a city boundary itself
              const isExactMatch = featurePostcode === postalCode;
              const isPostcodeType = resultType.includes("postcode");
              const notCityBoundary =
                !resultType.includes("city") &&
                !resultType.includes("district");

              return isExactMatch && isPostcodeType && notCityBoundary;
            });

            if (exactMatch) {
              console.log(
                `✓ Exact match for ${postalCode}:`,
                exactMatch.properties?.type,
                "City:",
                exactMatch.properties?.city,
              );
              return exactMatch;
            }

            // If no exact match, log and return null to use Overpass fallback
            console.warn(
              `✗ No exact postcode match for ${postalCode}, got:`,
              features[0]?.properties,
            );
            return null;
          } catch (error) {
            console.warn(
              `Geoapify failed for postal code ${postalCode}:`,
              error,
            );
            return null;
          }
        }),
      );

      const allFeatures = geoapifyFeatures.filter((f) => f?.geometry);

      if (allFeatures.length > 0) {
        // Check for duplicate geometries (indicates city boundary used for multiple postcodes)
        const geometryMap = new Map<string, string>();
        const duplicateGeometries = new Set<string>();

        allFeatures.forEach((feature) => {
          if (!feature) return;

          const geometryKey = JSON.stringify(feature.geometry.coordinates);
          if (geometryMap.has(geometryKey)) {
            duplicateGeometries.add(geometryKey);
            console.warn(
              "⚠️ Duplicate geometry detected for postcodes:",
              geometryMap.get(geometryKey),
              "and",
              feature.properties?.postcode,
            );
          } else {
            geometryMap.set(geometryKey, feature.properties?.postcode || "");
          }
        });

        // If any duplicates found, reject all Geoapify results and use Overpass
        if (duplicateGeometries.size > 0) {
          console.warn(
            `❌ Found ${duplicateGeometries.size} duplicate geometries - likely city boundaries. Falling back to Overpass for all postcodes.`,
          );
          // Return empty to trigger full Overpass fallback
          allFeatures.length = 0;
        } else {
          console.log(
            `Geoapify found ${allFeatures.length} unique postal code boundaries`,
          );
          return {
            type: "FeatureCollection",
            features: allFeatures,
          };
        }
      }

      console.log(
        "Geoapify returned no results, falling back to Overpass API...",
      );
    } catch (error) {
      console.warn("Geoapify failed, falling back to Overpass API:", error);
    }

    // Fallback to Overpass API
    console.log("Using Overpass API...");
    const endpoint = "https://overpass-api.de/api/interpreter";

    // Group postal codes by country to minimize the number of area lookups
    const postalCodesByCountry = postalCodesWithCountryCodes.reduce(
      (acc, { postalCode, countryCode }) => {
        if (!acc[countryCode]) acc[countryCode] = [];
        acc[countryCode].push(postalCode);
        return acc;
      },
      {} as Record<string, string[]>,
    );

    const queries = Object.entries(postalCodesByCountry).map(
      ([countryCode, postalCodes]) => {
        // Create a regular expression to match postal codes in the list
        const postalCodeRegex = postalCodes
          .map((code) => `^${code}$`)
          .join("|");

        return `
        area["ISO3166-1"="${countryCode}"][admin_level=2]->.searchArea;
        (
          relation["boundary"="postal_code"]["postal_code"~"${postalCodeRegex}"](area.searchArea);
          way["boundary"="postal_code"]["postal_code"~"${postalCodeRegex}"](area.searchArea);
          node["boundary"="postal_code"]["postal_code"~"${postalCodeRegex}"](area.searchArea);
        );
      `;
      },
    );

    const fullQuery = `
      [out:json][timeout:600];
      (
        ${queries.join("\n")}
      );
      out body;
      >;
      out skel qt;
    `;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: fullQuery,
      });

      if (!response.ok) {
        throw new Error(`Error querying Overpass API: ${response.statusText}`);
      }

      const data = await response.json();
      const geojson = osmtogeojson(data);

      if (geojson?.features) {
        const validFeatures = geojson.features.filter(
          (feature) => feature?.geometry && feature?.properties,
        );
        console.log(
          `Overpass API found ${validFeatures.length} valid features`,
        );
        return {
          type: "FeatureCollection",
          features: validFeatures,
        };
      }
      console.warn("No valid features found from Overpass API");
      return null;
    } catch (error) {
      console.error("Error fetching postal code boundaries:", error);
      return null;
    }
  };

  return { getPostalCodeBoundaries };
};
